
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Product } from "@/types/services";
import { useTranslation } from "react-i18next";

export const useDescriptionGeneration = (
  products: Product[],
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>,
  userProfession: string | null
) => {
  const { toast } = useToast();
  const { i18n } = useTranslation();
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [descriptionProductIndex, setDescriptionProductIndex] = useState<number | null>(null);

  const generateDescription = async (index: number) => {
    const product = products[index];
    if (!product.name) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a service name first",
      });
      return;
    }

    setIsGeneratingDescription(true);
    setDescriptionProductIndex(index);

    try {
      console.log('Generating description for service:', product.name);
      
      const { data, error } = await supabase.functions.invoke('generate-service-description', {
        body: { 
          serviceName: product.name,
          category: userProfession || 'Service',
          locale: i18n.language
        }
      });

      if (error) {
        console.error('Error generating description:', error);
        throw error;
      }

      if (!data?.description) {
        throw new Error('No description returned from the API');
      }

      const newProducts = [...products];
      newProducts[index] = {
        ...newProducts[index],
        description: data.description
      };
      setProducts(newProducts);

      toast({
        title: "Success",
        description: "Description generated successfully!",
      });
    } catch (error) {
      console.error('Error generating description:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to generate description. Please try again.",
      });
    } finally {
      setIsGeneratingDescription(false);
      setDescriptionProductIndex(null);
    }
  };

  return {
    isGeneratingDescription,
    descriptionProductIndex,
    generateDescription
  };
};
